import React, { useState, useMemo, useEffect } from 'react';
import { CheckCircle, Circle, ChevronDown, ChevronUp } from 'lucide-react';
import { SnippetsOutlined, BarChartOutlined } from '@ant-design/icons';
import { Card, Button, Typography } from "antd";
import { useTranslation, Trans } from 'react-i18next';
import BenImage from "../../../assets/ben.png";

const { Title, Text } = Typography;

const CheckList = () => {
    const { t, i18n } = useTranslation();

    const initialTodos = useMemo(() => [
        {
            id: 1,
            title: t('onboarding.todo1_title'),
            description: t('onboarding.todo1_description'),
            videoUrl: "https://www.youtube.com/embed/mqQIqnNXOGI?si=hod_ayF96u9suXKw",
            completed: false,
        },
        {
            id: 2,
            title: t('onboarding.todo2_title'),
            description: (
                <div style={{ display: "flex", alignItems: "flex-start", gap: "16px" }}>
                    <img
                        src={BenImage}
                        alt={t('onboarding.todo2_image_alt')}
                        style={{
                            width: "160px",
                            height: "160px",
                            borderRadius: "8px",
                            objectFit: "cover",
                        }}
                    />
                    <div>
                        <Text>
                            <Trans
                                i18nKey="onboarding.todo2_line1"
                                components={{ bold: <strong /> }}
                            />
                        </Text>
                        <br />
                        <br />
                        <Text>
                            {t('onboarding.todo2_line2')}
                        </Text>
                        <br />
                        <br />
                        <Text>
                            {t('onboarding.todo2_line3_before')}{' '}
                            <a
                                href="mailto:bbatoy@crg.ca"
                                style={{ color: "#467886", textDecoration: "none", fontWeight: "600" }}
                            >
                                bbatoy@crg.ca
                            </a>
                            {t('onboarding.todo2_line3_after')}
                        </Text>
                        <br />
                        <br />
                        <Text>
                            {t('onboarding.todo2_line4')}
                        </Text>
                    </div>
                </div>
            ),
            completed: false,
        },
        {
            id: 3,
            title: t('onboarding.todo3_title'),
            description: (
                <div>
                    <Text>
                        {t('onboarding.todo3_intro')}
                    </Text>
                    <br />
                    <br />
                    <Text style={{ fontWeight: "bold", fontSize: "16px" }}>{t('onboarding.todo3_step1_heading')}</Text>
                    <br />
                    <Text>{t('onboarding.todo3_step1_text')}</Text>
                    <br />
                    <br />
                    <Text style={{ fontWeight: "bold", fontSize: "16px" }}>{t('onboarding.todo3_step2_heading')}</Text>
                    <br />
                    <Text>
                        {t('onboarding.todo3_step2_intro')}
                        <ul style={{ marginTop: "10px", marginBottom: "10px" }}>
                            <li>{t('onboarding.todo3_step2_li1')}</li>
                            <li>{t('onboarding.todo3_step2_li2')}</li>
                            <li>{t('onboarding.todo3_step2_li3')}</li>
                            <li>{t('onboarding.todo3_step2_li4')}</li>
                        </ul>
                    </Text>
                    <br />
                    <Text style={{ fontWeight: "bold", fontSize: "16px" }}>{t('onboarding.todo3_step3_heading')}</Text>
                    <br />
                    <Text>
                        {t('onboarding.todo3_step3_before')}
                        <a
                            href="/surveys"
                            style={{
                                fontWeight: "bold",
                                color: "#467886",
                                textDecoration: "none",
                                display: "inline-flex",
                                alignItems: "center",
                                verticalAlign: "middle",
                                gap: "6px",
                            }}
                        >
                            <SnippetsOutlined style={{ fontSize: "15px", marginBottom: "3px" }} />
                            <span style={{ textDecoration: "underline" }}>{t('sidebar.surveys')}</span>
                        </a>
                        {t('onboarding.todo3_step3_after')}
                    </Text>
                </div>
            ),
            completed: false,
        },
        {
            id: 4,
            title: t('onboarding.todo4_title'),
            description: (
                <div>
                    <Text>
                        {t('onboarding.todo4_line1')}
                    </Text>
                    <br />
                    <br />
                    <Text>
                        {t('onboarding.todo4_line2_before')}
                        <a
                            href="/reports"
                            style={{
                                fontWeight: "bold",
                                color: "#467886",
                                textDecoration: "none",
                                display: "inline-flex",
                                alignItems: "center",
                                verticalAlign: "middle",
                                gap: "6px",
                            }}
                        >
                            <BarChartOutlined style={{ fontSize: "15px", marginBottom: "3px" }} />
                            <span>{t('sidebar.reports')}</span>
                        </a>
                        {t('onboarding.todo4_line2_after')}
                    </Text>
                </div>
            ),
            videoUrl: "https://www.youtube.com/embed/mqQIqnNXOGI?si=hod_ayF96u9suXKw",
            completed: false,
        },
    ], [t, i18n.language]);

    const [todos, setTodos] = useState(initialTodos);

    useEffect(() => {
        setTodos(initialTodos);
    }, [initialTodos]);

    const toggleComplete = (id) => {
        setTodos(
            (prev) =>
                prev.map((todo) =>
                    todo.id === id ? { ...todo, completed: !todo.completed } : todo
                )
        );
    };

    const [activePanel, setActivePanel] = useState(null);

    const handleToggle = (id) => {
        setActivePanel(activePanel === id ? null : id);
    };

    return (
        <div
            style={{
                padding: "20px",
                backgroundColor: "#f6f6f6",
                borderRadius: "8px",
                boxShadow: "0 3px 8px rgba(0, 0, 0, .2)",
            }}
        >
            <div
                style={{
                    maxWidth: "1200px",
                    margin: "0 auto",
                    borderRadius: "8px",
                }}
            >
                <Title
                    level={2}
                    style={{
                        textAlign: "center",
                        marginBottom: "38px",
                        fontSize: "20px",
                    }}
                >
                    {t('onboarding.checklist_title')}
                </Title>

                {todos.map((todo) => (
                    <Card
                        key={todo.id}
                        style={{
                            marginBottom: "24px",
                            borderRadius: "8px",
                            boxShadow: "0px 4px 10px rgba(0, 0, 0, 0.1)",
                        }}
                        bodyStyle={{ padding: "12px" }}
                        hoverable
                    >
                        <div style={{ display: "flex", alignItems: "center" }}>
                            <Button
                                type="text"
                                icon={
                                    todo.completed ? (
                                        <CheckCircle style={{ color: "green", fontSize: "20px" }} />
                                    ) : (
                                        <Circle style={{ color: "gray", fontSize: "20px" }} />
                                    )
                                }
                                onClick={() => toggleComplete(todo.id)}
                                style={{ marginRight: "16px" }}
                            />
                            <div style={{ flexGrow: 1 }}>
                                <Title
                                    level={4}
                                    style={{
                                        fontSize: "14px",
                                        margin: 0,
                                        textDecoration: todo.completed ? "line-through" : "none",
                                        color: todo.completed ? "gray" : "inherit",
                                    }}
                                >
                                    {todo.title}
                                </Title>
                            </div>
                            <Button
                                type="text"
                                icon={
                                    activePanel === todo.id ? (
                                        <ChevronUp
                                            style={{
                                                fontSize: "20px",
                                                color: "#CBD5E1",
                                                background: "#FFF",
                                                borderColor: "none",
                                            }}
                                        />
                                    ) : (
                                        <ChevronDown
                                            style={{
                                                fontSize: "20px",
                                                color: "#CBD5E1",
                                                background: "#FFF",
                                                borderColor: "none",
                                            }}
                                        />
                                    )
                                }
                                onClick={() => handleToggle(todo.id)}
                                style={{ marginTop: "8px" }}
                            />
                        </div>

                        {activePanel === todo.id && (
                            <div
                                style={{
                                    margin: "12px auto",
                                }}
                            >
                                <Text
                                    style={{
                                        fontSize: "14px",
                                        marginBottom: "20px",
                                        marginTop: "10px",
                                    }}
                                >
                                    {todo.description}
                                </Text>

                                {todo.videoUrl && (
                                <div  style={{maxWidth: "800px", margin: "auto"}}>
                                    <iframe
                                        title={t('onboarding.video_iframe_title')}
                                        width="100%"
                                        height="450"
                                        src={todo.videoUrl}
                                        frameBorder="0"
                                        allow="autoplay; encrypted-media; picture-in-picture"
                                        allowFullScreen
                                        style={{
                                            borderRadius: "8px",
                                            marginTop: "10px",
                                            boxShadow: "0px 2px 8px rgba(0, 0, 0, 0.1)",
                                        }}
                                    ></iframe>
                                </div>
                                )}
                            </div>
                        )}
                    </Card>
                ))}
            </div>
        </div>
    );
};

export default CheckList;
